import { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { API_BASE_URL } from '../config';
import { usePermissions } from '../context/PermissionsContext';
import { useTenant } from '../context/TenantContext';
import { PERMISSIONS } from '../constants/permissions';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import LoadingSpinner, { LoadingButton } from '../components/LoadingSpinner';
import './Activities.css';
import './Branding.css';

const DEFAULT_HEADER_FILL = '#356167';

function BrandingPage() {
  const { hasPermission } = usePermissions();
  const { currentTenant } = useTenant();
  const canUpdate = hasPermission(PERMISSIONS.SETTINGS_UPDATE);

  const [headerFill, setHeaderFill] = useState(DEFAULT_HEADER_FILL);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusTone, setStatusTone] = useState('success');

  const brandingQs = useCallback(() => {
    if (!currentTenant?.id) return '';
    return `?tenant_id=${encodeURIComponent(currentTenant.id)}`;
  }, [currentTenant?.id]);

  const revokePreview = useCallback((url) => {
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
  }, []);

  const loadLogoBlob = useCallback(async () => {
    const response = await fetchWithAuth(`${API_BASE_URL}/tenant-branding/logo${brandingQs()}`);
    if (!response.ok) return null;
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }, [brandingQs]);

  const loadBranding = useCallback(async () => {
    setIsLoading(true);
    setStatusMessage('');
    // Clear previous tenant's branding immediately so it cannot linger
    setHeaderFill(DEFAULT_HEADER_FILL);
    setHasLogo(false);
    setPendingFile(null);
    setLogoPreviewUrl((prev) => {
      revokePreview(prev);
      return null;
    });

    if (!currentTenant?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/tenant-branding${brandingQs()}`);
      if (!response.ok) {
        throw new Error('Failed to load branding');
      }
      const data = await response.json();
      setHeaderFill(data.headerFill || DEFAULT_HEADER_FILL);
      setHasLogo(Boolean(data.hasLogo));
      setPendingFile(null);

      if (data.hasLogo) {
        const objectUrl = await loadLogoBlob();
        if (objectUrl) setLogoPreviewUrl(objectUrl);
      }
    } catch (error) {
      console.error(error);
      setStatusTone('danger');
      setStatusMessage('Could not load branding for this tenant.');
    } finally {
      setIsLoading(false);
    }
  }, [brandingQs, currentTenant?.id, loadLogoBlob, revokePreview]);

  useEffect(() => {
    loadBranding();
    return () => {
      setLogoPreviewUrl((prev) => {
        revokePreview(prev);
        return null;
      });
    };
  }, [currentTenant?.id, loadBranding, revokePreview]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setLogoPreviewUrl((prev) => {
      revokePreview(prev);
      return URL.createObjectURL(file);
    });
    setHasLogo(true);
  };

  const handleRemoveLogo = async () => {
    if (!canUpdate || !currentTenant?.id) return;
    setIsSaving(true);
    setStatusMessage('');
    try {
      if (pendingFile) {
        setPendingFile(null);
        setLogoPreviewUrl((prev) => {
          revokePreview(prev);
          return null;
        });
        setHasLogo(false);
        setStatusTone('success');
        setStatusMessage('Pending logo cleared.');
        return;
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/tenant-branding/logo${brandingQs()}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to remove logo');
      }
      setPendingFile(null);
      setHasLogo(false);
      setLogoPreviewUrl((prev) => {
        revokePreview(prev);
        return null;
      });
      setStatusTone('success');
      setStatusMessage('Logo removed.');
    } catch (error) {
      console.error(error);
      setStatusTone('danger');
      setStatusMessage('Failed to remove logo.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canUpdate || !currentTenant?.id) return;
    if (!/^#[0-9A-Fa-f]{6}$/.test(headerFill)) {
      setStatusTone('danger');
      setStatusMessage('Header fill must be a hex color like #356167.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    try {
      const formData = new FormData();
      formData.append('header_fill', headerFill);
      formData.append('tenant_id', currentTenant.id);
      if (pendingFile) {
        formData.append('logo', pendingFile);
      }

      const response = await fetchWithAuth(`${API_BASE_URL}/tenant-branding${brandingQs()}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save branding');
      }

      setPendingFile(null);
      setStatusTone('success');
      setStatusMessage('Branding saved for this tenant.');
      await loadBranding();
    } catch (error) {
      console.error(error);
      setStatusTone('danger');
      setStatusMessage(error.message || 'Failed to save branding.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="branding-page branding-page--loading">
        <LoadingSpinner size="md" variant="primary" message="Loading branding…" />
      </div>
    );
  }

  return (
    <div className="branding-page">
      <div className="page-hero">
        <div className="page-hero-text">
          <h1 className="page-hero-title">Branding</h1>
          <p className="page-hero-subtitle">
            Configure branding for this tenant&apos;s quotation PDFs. The logo also appears on the quotation page.
          </p>
        </div>
      </div>

      <div className="branding-grid">
        <section className="branding-card">
          <h2>Quotation logo</h2>
          <p className="branding-help">
            Shown top-right on the quotation page and on each PDF page for this tenant.
            Prefer a PNG with a transparent background. There is no default logo — only what you upload here is used.
          </p>

          <div className="branding-logo-preview">
            <div className="branding-logo-preview__mock">
              <span className="branding-logo-preview__label">Quotation header</span>
              {logoPreviewUrl ? (
                <img src={logoPreviewUrl} alt="Tenant quotation logo" />
              ) : (
                <span className="branding-logo-preview__empty">No logo</span>
              )}
            </div>
          </div>

          <Form.Group controlId="brandingLogo" className="mb-3">
            <Form.Label>Upload logo</Form.Label>
            <Form.Control
              type="file"
              accept="image/png,image/jpeg,image/webp"
              disabled={!canUpdate || isSaving}
              onChange={handleFileChange}
            />
            <Form.Text>PNG, JPEG, or WebP — max 2MB.</Form.Text>
          </Form.Group>

          {(hasLogo || pendingFile) && (
            <Button
              variant="outline-danger"
              size="sm"
              disabled={!canUpdate || isSaving}
              onClick={handleRemoveLogo}
            >
              Remove logo
            </Button>
          )}
        </section>

        <section className="branding-card">
          <h2>PDF table header fill</h2>
          <p className="branding-help">
            Applied to table header rows in the printed quotation PDF only (not on the quotation page).
          </p>

          <div className="branding-color-row">
            <Form.Control
              type="color"
              value={headerFill}
              disabled={!canUpdate || isSaving}
              onChange={(e) => setHeaderFill(e.target.value)}
              title="PDF header fill"
              style={{ width: 56, height: 40, padding: 4 }}
            />
            <Form.Control
              type="text"
              value={headerFill}
              disabled={!canUpdate || isSaving}
              onChange={(e) => setHeaderFill(e.target.value)}
              placeholder="#356167"
              style={{ maxWidth: 140 }}
            />
          </div>

          <div
            className="branding-header-swatch"
            style={{
              background: headerFill,
              color: (() => {
                const hex = String(headerFill || '').replace('#', '');
                if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return '#fff';
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#141414' : '#fff';
              })(),
            }}
          >
            Sample PDF table header
          </div>
        </section>
      </div>

      <div className="branding-actions">
        <LoadingButton
          variant="primary"
          className="btn btn-primary"
          disabled={!canUpdate}
          isLoading={isSaving}
          loadingText="Saving…"
          onClick={handleSave}
        >
          Save branding
        </LoadingButton>
        {!canUpdate && (
          <span className="branding-permission-note">
            You need settings update permission to change branding.
          </span>
        )}
      </div>

      {statusMessage && (
        <div className={`branding-status branding-status--${statusTone}`}>
          {statusMessage}
        </div>
      )}
    </div>
  );
}

export default BrandingPage;
